import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { isBefore } from 'date-fns/fp';
import { Downloads } from '#api';
import { trackError } from '#api/errors.ts';
import type {
  Architecture,
  ClientInfo,
  CurrentVersion,
  DesktopOSName,
  DownloadInfo,
  OSGroup,
} from '#dn-types/downloads.ts';
import { buildDownloadCommand } from '#util/build-download-command.ts';

const MAIN_ARCHS: Architecture[] = ['AMD64', 'ARMv7', 'ARM64', 'Universal', 'Universal (DMG)'];

const downloadsQueryOptions = queryOptions({
  queryKey: ['downloads'],
  queryFn: ({ signal }) => Downloads.getDownloads({ signal }),
});

// The enroll and downloads views share one cache entry and differ only in `select`, so a page
// that renders both makes a single GET /v1/downloads.
export const latestDownloadsQueryOptions = queryOptions({
  ...downloadsQueryOptions,
  select(data) {
    return selectForEnrollPage(data);
  },
});

/**
 * Fetch information about client latest downloads and mobile/container urls
 */
export function useSuspenseLatestDownloads() {
  return useSuspenseQuery(latestDownloadsQueryOptions);
}

function selectForEnrollPage({ data }: { data: Downloads.GetDownloadsResult }) {
  const { latest } = data.dnclient;

  return {
    Linux: [
      {
        key: 'linux-386',
        label: '386',
        type: 'download',
        url: latest['linux-386'],
        download: buildDownloadCommand('linux', latest['linux-386']),
      },
      {
        key: 'linux-amd64',
        label: 'AMD64',
        type: 'download',
        url: latest['linux-amd64'],
        download: buildDownloadCommand('linux', latest['linux-amd64']),
      },
      {
        key: 'linux-arm64',
        label: 'ARM64',
        type: 'download',
        url: latest['linux-arm64'],
        download: buildDownloadCommand('linux', latest['linux-arm64']),
      },
      {
        key: 'linux-armv5',
        label: 'ARMv5',
        type: 'download',
        url: latest['linux-armv5'],
        download: buildDownloadCommand('linux', latest['linux-armv5']),
      },
      {
        key: 'linux-armv6',
        label: 'ARMv6',
        type: 'download',
        url: latest['linux-armv6'],
        download: buildDownloadCommand('linux', latest['linux-armv6']),
      },
      {
        key: 'linux-armv7',
        label: 'ARMv7',
        type: 'download',
        url: latest['linux-armv7'],
        download: buildDownloadCommand('linux', latest['linux-armv7']),
      },
      {
        key: 'linux-mips',
        label: 'MIPS',
        type: 'download',
        url: latest['linux-mips'],
        download: buildDownloadCommand('linux', latest['linux-mips']),
      },
      {
        key: 'linux-mips-softfloat',
        label: 'MIPS softfloat',
        type: 'download',
        url: latest['linux-mips-softfloat'],
        download: buildDownloadCommand('linux', latest['linux-mips-softfloat']),
      },
      {
        key: 'linux-mips64',
        label: 'MIPS 64-bit',
        type: 'download',
        url: latest['linux-mips64'],
        download: buildDownloadCommand('linux', latest['linux-mips64']),
      },
      {
        key: 'linux-mips64le',
        label: 'MIPS little endian',
        type: 'download',
        url: latest['linux-mips64le'],
        download: buildDownloadCommand('linux', latest['linux-mips64le']),
      },
      {
        key: 'linux-ppc64le',
        label: 'PowerPC 64-bit little endian',
        type: 'download',
        url: latest['linux-ppc64le'],
        download: buildDownloadCommand('linux', latest['linux-ppc64le']),
      },
      {
        key: 'linux-riscv64',
        label: 'RISC-V 64-bit',
        type: 'download',
        url: latest['linux-riscv64'],
        download: buildDownloadCommand('linux', latest['linux-riscv64']),
      },
    ],
    macOS: [
      {
        // deprecated
        key: 'macos-universal',
        label: 'Universal',
        type: 'download',
        url: latest['macos-universal'],
        download: buildDownloadCommand('macos', latest['macos-universal']),
      },
      {
        // deprecated
        key: 'macos-universal-dmg',
        label: 'Universal (DMG)',
        type: 'download',
        url: latest['macos-universal-dmg'],
        download: buildDownloadCommand('macos', latest['macos-universal-dmg']),
      },
      {
        key: 'macos-universal-server',
        label: 'Universal Server',
        type: 'url',
        url: latest['macos-universal-server'],
      },
      {
        key: 'macos-universal-server-dmg',
        label: 'Universal Server (DMG)',
        type: 'url',
        url: latest['macos-universal-server-dmg'],
      },
      {
        key: 'macos-universal-desktop',
        label: 'Universal Desktop',
        type: 'url',
        url: latest['macos-universal-desktop'],
      },
    ],
    Windows: [
      {
        // deprecated
        key: 'windows-amd64',
        label: 'AMD64',
        type: 'download',
        url: latest['windows-amd64'],
        download: buildDownloadCommand('windows', latest['windows-amd64']),
      },
      {
        // deprecated
        key: 'windows-arm64',
        label: 'ARM64',
        type: 'download',
        url: latest['windows-arm64'],
        download: buildDownloadCommand('windows', latest['windows-arm64']),
      },
      {
        key: 'windows-amd64-server',
        label: 'AMD64 Server',
        type: 'download',
        url: latest['windows-amd64-server'],
        download: buildDownloadCommand('windows', latest['windows-amd64-server']),
      },
      {
        key: 'windows-arm64-server',
        label: 'ARM64 Server',
        type: 'download',
        url: latest['windows-arm64-server'],
        download: buildDownloadCommand('windows', latest['windows-arm64-server']),
      },
      {
        key: 'windows-amd64-desktop',
        label: 'AMD64 Desktop',
        type: 'download',
        url: latest['windows-amd64-desktop'],
        download: buildDownloadCommand('windows', latest['windows-amd64-desktop']),
      },
      {
        key: 'windows-arm64-desktop',
        label: 'ARM64 Desktop',
        type: 'download',
        url: latest['windows-arm64-desktop'],
        download: buildDownloadCommand('windows', latest['windows-arm64-desktop']),
      },
    ],
    Mobile: [
      { key: 'android', label: 'Android', type: 'url', url: data.mobile.android },
      { key: 'ios', label: 'iOS', type: 'url', url: data.mobile.ios },
    ],
    Other: [
      {
        key: 'freebsd-amd64',
        label: 'FreeBSD AMD64',
        type: 'download',
        url: latest['freebsd-amd64'],
        download: buildDownloadCommand('freebsd', latest['freebsd-amd64']),
      },
      {
        key: 'freebsd-arm64',
        label: 'FreeBSD ARM64',
        type: 'download',
        url: latest['freebsd-arm64'],
        download: buildDownloadCommand('freebsd', latest['freebsd-arm64']),
      },
      { key: 'docker', label: 'Docker', type: 'url', url: data.container.docker },
      { key: 'docker-compose', label: 'Docker compose', type: 'url', url: data.container.docker },
    ],
  } as const satisfies Record<OSGroup, ClientInfo[]>;
}

export type DownloadsMap = ReturnType<typeof selectForEnrollPage>;

/**
 * Fetch information about client downloads, but transform it in a way that can be consumed by our downloads page
 */
export const downloadsTransformedQueryOptions = queryOptions({
  ...downloadsQueryOptions,
  select: transformDownloads,
});

function transformDownloads({ data }: { data: Downloads.GetDownloadsResult }) {
  const downloadsByVersion = Object.fromEntries(
    Object.entries(data.dnclient)
      .filter(([ver]) => ver !== 'latest')
      .map(([ver, info]) => {
        const architectures = Object.entries(info);
        const plats = architectures
          .map(([key, url]) => ({
            OSName: getOSFromKey(key),
            architectureName: getArchNameFromKey(key),
            url,
          }))
          .filter((plat) => plat.OSName && plat.architectureName) as DownloadInfo[];
        return [ver, plats] as const;
      })
  );

  const latestDNClientVersion = data.versionInfo.latest.dnclient;
  const { releaseDate: latestReleaseDate } = assert(
    data.versionInfo.dnclient[latestDNClientVersion],
    'Could not determine latest download version.'
  );
  const latestPlatformList = assert(downloadsByVersion[latestDNClientVersion]);
  const mainDownloads = makeMainDownloads(latestPlatformList);
  const current = {
    releaseDate: latestReleaseDate,
    version: latestDNClientVersion,
    ...mainDownloads,
  } satisfies CurrentVersion;

  // Sort newest to oldest
  const allVersions = Object.entries(data.versionInfo.dnclient)
    .sort(([, infoA], [, infoB]) => {
      const dateA = new Date(infoA.releaseDate);
      const dateB = new Date(infoB.releaseDate);
      return isBefore(dateA)(dateB) ? -1 : 1;
    })
    .map(([v, info]) => [v, info.releaseDate] as const);

  const all = allVersions
    .map(([version, date]) => {
      const downloads = downloadsByVersion[version];
      if (!downloads) return undefined;

      return { version, date, downloads };
    })
    .filter(Boolean);

  const mobile = [
    {
      OSName: 'iOS' as const,
      url: data.mobile.ios,
    },
    {
      OSName: 'Android' as const,
      url: data.mobile.android,
    },
  ];

  const container = [
    {
      OSName: 'Docker' as const,
      url: data.container.docker,
    },
  ];

  return {
    dnclient: {
      current,
      all,
    },
    mobile,
    container,
  };
}

function getOSFromKey(key: string): DesktopOSName | undefined {
  const os = key.split('-')[0];
  switch (os) {
    case 'linux':
      return 'Linux';
    case 'windows':
      return 'Windows';
    case 'macos':
      return 'macOS';
    case 'freebsd':
      return 'FreeBSD';
    case undefined:
    default:
      trackError(new Error(`Could not determine OS for key: ${key}`));
      return undefined;
  }
}

function getArchNameFromKey(key: string): Architecture | undefined {
  const os = key.split('-')[0];
  const arch = key.replace(`${os}-`, '');
  switch (arch) {
    case 'amd64':
      return 'AMD64';
    case 'arm64':
      return 'ARM64';
    case '386':
      return '386';
    case 'armv5':
      return 'ARMv5';
    case 'armv6':
      return 'ARMv6';
    case 'armv7':
      return 'ARMv7';
    case 'mips':
      return 'MIPS';
    case 'mips-softfloat':
      return 'MIPS softfloat';
    case 'mips64':
      return 'MIPS 64-bit';
    case 'mips64le':
      return 'MIPS 64-bit little endian';
    case 'mipsle':
      return 'MIPS little endian';
    case 'ppc64le':
      return 'PowerPC 64-bit little endian';
    case 'riscv64':
      return 'RISC-V 64-bit';
    case 'universal': // deprecated
      return 'Universal';
    case 'universal-dmg': // deprecated
      return 'Universal (DMG)';
    case 'universal-server':
      return 'Universal Server';
    case 'universal-server-dmg':
      return 'Universal Server (DMG)';
    case 'universal-desktop':
      return 'Universal Desktop';
    case 'amd64-desktop':
      return 'AMD64 Desktop';
    case 'arm64-desktop':
      return 'ARM64 Desktop';
    case 'amd64-server':
      return 'AMD64 Server';
    case 'arm64-server':
      return 'ARM64 Server';
    default:
      trackError(new Error(`Could not determine architecture for architecture: ${arch}`));
      return undefined;
  }
}

type MainDownloads = Readonly<{
  Linux: DownloadInfo[];
  macOS: DownloadInfo[];
  Windows: DownloadInfo[];
}>;

function makeMainDownloads(data: DownloadInfo[]): MainDownloads {
  return {
    Linux: data
      .filter((p) => p.OSName === 'Linux' && MAIN_ARCHS.includes(p.architectureName))
      // Special-case sort to keep the order the same as it used to be
      .sort((a, b) => {
        if (a.architectureName === 'ARMv7' && b.architectureName === 'ARM64') return -1;
        return 0;
      }),
    macOS: data.filter((p) => p.OSName === 'macOS' && MAIN_ARCHS.includes(p.architectureName)),
    Windows: data.filter((p) => p.OSName === 'Windows' && MAIN_ARCHS.includes(p.architectureName)),
  };
}
